import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { ModelType } from 'components/Pages/Jobs/inferenceFieldsOptions';
import {
  getModelsTypeByModelInferences,
  getOrchestrationKey,
  ModelInference,
  ResultNameOrchestrations,
} from 'interfaces/calculateFeatures';
import { CellConfig, PipsResultsConfig } from 'interfaces/inferenceArtifacts';
import { CellDetectionDependentJob, JobType } from 'interfaces/job';
import { CellDetectionDependentJobConfig, MapArtifactToSlides } from 'interfaces/jobs/basic';
import {
  compact,
  filter,
  first,
  flatMap,
  forEach,
  get,
  includes,
  intersection,
  isEmpty,
  map,
  size,
  some,
  values,
  xor,
} from 'lodash';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { humanize } from 'utils/helpers';
import { OrchestrationBySlideByResultName, useOrchestrationSelections } from 'utils/orchestrationSelections';
import { CasesParams } from 'utils/useCasesParams/types';
import * as yup from 'yup';
import { ActionStepper } from '../ActionStepper';
import { JobWithRebuild } from '../JobWithRebuild';
import { LoadFromPreviousJobStep } from '../LoadFromPreviousJobStep';
import { usePreviousJobsAvailability } from '../LoadFromPreviousJobStep/usePreviousJobsAvailability';
import InferenceModelsForSlides from '../PostProcessingAndCalculateFeatures/InferenceModelsForSlides';
import { getDetectionLabelOptions } from '../PostProcessingAndCalculateFeatures/PostProcessingActions/utils';
import { useArtifactResultsQuery } from '../PostProcessingAndCalculateFeatures/useArtifactsResultsQuery';
import {
  buildSelectedInferenceOrchestrationsFromArtifacts,
  findByOrchestrationResultArtifactUrlPattern,
  findOrchestrationByArtifactUrlPattern,
} from '../PostProcessingAndCalculateFeatures/utils';
import { ExecutionRequirementsPanel } from '../requirements/ExecutionRequirementsPanel';
import { ExecutionRequirementType, useExecutionRequirements } from '../requirements/useExecutionRequirements';
import { SelectModelStep } from '../SelectModelStep';
import { getMapArtifactToSlides, JobNameAndDescriptionStep } from '../utils';

export interface GenericInferenceDependantJobValues {
  jobName: string;
  jobDescription: string;
  stainTypeId: string;
  configParams: CellDetectionDependentJobConfig;
}

const defaultValues: GenericInferenceDependantJobValues = {
  jobName: '',
  jobDescription: '',
  stainTypeId: '',
  configParams: {
    modelPath: '',
    cellDetectionArtifactUrlPatterns: [],
    includeCellClasses: [],
  },
};

const validationSchema = [
  yup.object({}),
  yup.object({}),
  yup.object().shape({
    stainTypeId: yup.string().required('Stain is required'),
    configParams: yup.object().shape({
      modelPath: yup.string().required('Model Path is required'),
    }),
  }),
  yup.object().shape({
    configParams: yup.object().shape({
      cellDetectionArtifactUrlPatterns: yup
        .array()
        .of(
          yup.object().shape({
            artifactUrlPattern: yup.string().required('Cell detection source is required'),
            slideIds: yup
              .array()
              .of(yup.string().required('A selected slide is required'))
              .min(1, 'The cell detection source must include a slide'),
          })
        )
        .min(1, 'Select one cell detection source for each slide')
        .required('Select one cell detection source for each slide'),
    }),
  }),
];

export interface RunInferenceDependentJobProps {
  casesParams: CasesParams;
  jobType: JobType;
  modelType: ModelType;
  onSubmit: (data: GenericInferenceDependantJobValues) => void;
  jobId?: string;
}

export const getCellDetectionArtifactSelectionError = (
  slideIds: string[],
  cellDetectionArtifactUrlPatterns: MapArtifactToSlides[]
) => {
  const { missingSourceCount, multipleSourceCount } = getCellDetectionSourceIssueCounts(
    slideIds,
    cellDetectionArtifactUrlPatterns
  );

  if (missingSourceCount > 0) {
    return 'Choose one cell detection source per slide.';
  }

  if (multipleSourceCount > 0) {
    return 'Some slides have multiple cell detection sources. Keep one source per slide.';
  }

  return undefined;
};

export const getCellDetectionSourceIssueCounts = (
  slideIds: string[],
  cellDetectionArtifactUrlPatterns: MapArtifactToSlides[]
) => {
  const missingSourceCount = size(
    filter(
      slideIds,
      (slideId) =>
        !some(cellDetectionArtifactUrlPatterns, (artifactUrlPatternWithSlides) =>
          includes(artifactUrlPatternWithSlides.slideIds, slideId)
        )
    )
  );
  const multipleSourceCount = size(
    filter(
      slideIds,
      (slideId) =>
        size(
          filter(cellDetectionArtifactUrlPatterns, (artifactUrlPatternWithSlides) =>
            includes(artifactUrlPatternWithSlides.slideIds, slideId)
          )
        ) > 1
    )
  );

  return { missingSourceCount, multipleSourceCount };
};

export const filterArtifactsToSlideIds = <T extends ModelInference | ResultNameOrchestrations>(
  slideIds: string[],
  artifacts: T[] = []
): T[] =>
  filter(
    map(artifacts, (artifact) => ({
      ...artifact,
      orchestrations: compact(
        map(artifact.orchestrations, (orchestration) => {
          const matchingSlideIds = intersection(orchestration.slideIds, slideIds);
          return isEmpty(matchingSlideIds) ? undefined : { ...orchestration, slideIds: matchingSlideIds };
        })
      ),
    })),
    (artifact) => !isEmpty(artifact.orchestrations)
  ) as T[];

const buildSelectedPipsOrchestrationsFromArtifacts = (
  artifactUrlPatternsWithSlides: MapArtifactToSlides[],
  pipsResultsArtifacts: ResultNameOrchestrations[]
): OrchestrationBySlideByResultName => {
  const selectedPipsOrchestrations: OrchestrationBySlideByResultName = {};

  forEach(artifactUrlPatternsWithSlides, ({ artifactUrlPattern, slideIds }) => {
    const resultGroup = findByOrchestrationResultArtifactUrlPattern(pipsResultsArtifacts, artifactUrlPattern);
    const orchestration = findOrchestrationByArtifactUrlPattern(resultGroup?.orchestrations, artifactUrlPattern);

    if (!resultGroup || !orchestration) {
      return;
    }

    const resultKey = getOrchestrationKey({
      resultName: resultGroup.resultName,
      resultType: resultGroup.resultType,
    }) as string;

    forEach(slideIds, (slideId) => {
      selectedPipsOrchestrations[slideId] = {
        ...selectedPipsOrchestrations[slideId],
        [resultKey]: { orchestration },
      };
    });
  });

  return selectedPipsOrchestrations;
};

export const getCommonCellClasses = (
  selectedOrchestrations: ReturnType<typeof useOrchestrationSelections>['selectedOrchestrations'],
  selectedPipsOrchestrations: ReturnType<typeof useOrchestrationSelections>['selectedPipsOrchestrations']
) => {
  const classesBySelectedSource = [
    ...flatMap(values(selectedOrchestrations), (orchestrationByType) =>
      map(values(orchestrationByType), ({ model }) => model?.classes ?? [])
    ),
    ...flatMap(values(selectedPipsOrchestrations), (orchestrationByResultName) =>
      map(values(orchestrationByResultName), ({ orchestration }) =>
        map(getDetectionLabelOptions(orchestration?.params?.data ?? []), 'id')
      )
    ),
  ];

  if (isEmpty(classesBySelectedSource)) {
    return [[], []] as const;
  }

  let commonClasses = first(classesBySelectedSource) ?? [];
  const missingClasses: string[] = [];

  forEach(classesBySelectedSource, (classes) => {
    const xorValues = xor(commonClasses, classes);
    if (!isEmpty(xorValues)) {
      missingClasses.push(...xorValues);
    }
    commonClasses = intersection(commonClasses, classes);
  });

  return [commonClasses, missingClasses] as const;
};

export const RunCellDetectionDependentJob = (props: RunInferenceDependentJobProps): ReactElement => {
  const { casesParams, jobType, modelType, onSubmit, jobId } = props;
  const studyId = casesParams.filters?.studyId;
  const orchestrationSelectionState = useOrchestrationSelections();
  const {
    selectedOrchestrations,
    setSelectedOrchestrations,
    selectedPipsOrchestrations,
    setSelectedPipsOrchestrations,
    setSelectedOrchestrationsByTypeByStain,
    updateSelectedPipsOrchestrations,
  } = orchestrationSelectionState;

  const [selectedJobId, setSelectedJobId] = useState<string>(jobId ?? undefined);
  const [isLoadingSelectedPreviousJob, setIsLoadingSelectedPreviousJob] = useState(false);
  const [pendingPreviousJobInferenceSelections, setPendingPreviousJobInferenceSelections] = useState<
    MapArtifactToSlides[] | null
  >(null);
  const [pendingPreviousJobPipsSelections, setPendingPreviousJobPipsSelections] = useState<
    MapArtifactToSlides[] | null
  >(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});
  const currentValidationSchema = validationSchema[activeStep];
  const form = useForm<GenericInferenceDependantJobValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
    defaultValues: defaultValues,
  });
  useTrackActionFormDirty(form.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = form;
  const executionRequirements = useExecutionRequirements([
    {
      type: ExecutionRequirementType.CloudOptimizedSlides,
      label: 'Cloud-optimized slides',
      casesParams,
      slideStainType: watch('stainTypeId'),
      enabled: Boolean(studyId),
    },
  ]);

  const [commonCellClasses, missingCellClasses] = useMemo(
    () => getCommonCellClasses(selectedOrchestrations, selectedPipsOrchestrations),
    [selectedOrchestrations, selectedPipsOrchestrations]
  );

  const { data: slides, isFetching: isSlidesFetching } = useQuery({
    queryKey: ['slidesDataForArtifactsResults', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    enabled: Boolean(studyId),
  });

  const selectedStainTypeId = watch('stainTypeId');
  const slidesForSelectedStain = useMemo(
    () => filter(slides, { stainingType: selectedStainTypeId }),
    [selectedStainTypeId, slides]
  );
  const selectedStainSlideIds = useMemo(() => map(slidesForSelectedStain, 'slideId'), [slidesForSelectedStain]);
  const cellDetectionArtifactUrlPatterns = useMemo(
    () => [
      ...getMapArtifactToSlides(selectedStainSlideIds, selectedOrchestrations),
      ...getMapArtifactToSlides(selectedStainSlideIds, selectedPipsOrchestrations),
    ],
    [selectedOrchestrations, selectedPipsOrchestrations, selectedStainSlideIds]
  );
  const cellDetectionSelectionError = useMemo(
    () => getCellDetectionArtifactSelectionError(selectedStainSlideIds, cellDetectionArtifactUrlPatterns),
    [cellDetectionArtifactUrlPatterns, selectedStainSlideIds]
  );
  const { missingSourceCount, multipleSourceCount } = useMemo(
    () => getCellDetectionSourceIssueCounts(selectedStainSlideIds, cellDetectionArtifactUrlPatterns),
    [cellDetectionArtifactUrlPatterns, selectedStainSlideIds]
  );
  const incompleteStepsMessage = missingSourceCount
    ? `Missing cell detection sources for ${missingSourceCount} slide${missingSourceCount === 1 ? '' : 's'}.`
    : multipleSourceCount
    ? `Multiple cell detection sources selected for ${multipleSourceCount} slide${
        multipleSourceCount === 1 ? '' : 's'
      }.`
    : false;

  const validateSlidesResultsCount = () => {
    if (cellDetectionSelectionError) {
      setError(
        'configParams.cellDetectionArtifactUrlPatterns',
        {
          type: 'manual',
          message: cellDetectionSelectionError,
        },
        { shouldFocus: true }
      );
      return false;
    }
    clearErrors('configParams.cellDetectionArtifactUrlPatterns');
    return true;
  };

  const checkValidationAndSetIsStepFailed = async (stepIndex: number) => {
    let isValid = await trigger();

    if (activeStep === 3) {
      isValid = validateSlidesResultsCount() && isValid;
    }

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));
    return isValid;
  };

  const cellDetectionArtifactsParams = {
    ...casesParams,
    slideIdsToInclude: selectedStainSlideIds,
  };

  const {
    data: inferenceArtifacts,
    isWaitingForResults: isWaitingForInferenceArtifacts,
    isError: isInferenceArtifactsError,
  } = useArtifactResultsQuery<ModelInference>({
    caseParams: cellDetectionArtifactsParams,
    config: CellConfig,
    enabled: !isEmpty(selectedStainTypeId) && !isEmpty(selectedStainSlideIds),
  });
  const {
    data: pipsResultsArtifacts,
    isWaitingForResults: isWaitingForPipsResults,
    isError: isPipsResultsArtifactsError,
  } = useArtifactResultsQuery<ResultNameOrchestrations>({
    caseParams: cellDetectionArtifactsParams,
    config: PipsResultsConfig,
    enabled: !isEmpty(selectedStainTypeId) && !isEmpty(selectedStainSlideIds),
  });
  const inferenceArtifactsForSelectedSlides = useMemo(
    () => filterArtifactsToSlideIds(selectedStainSlideIds, inferenceArtifacts),
    [inferenceArtifacts, selectedStainSlideIds]
  );
  const pipsResultsArtifactsForSelectedSlides = useMemo(
    () => filterArtifactsToSlideIds(selectedStainSlideIds, pipsResultsArtifacts),
    [pipsResultsArtifacts, selectedStainSlideIds]
  );

  useEffect(() => {
    if (
      pendingPreviousJobInferenceSelections === null ||
      isWaitingForInferenceArtifacts ||
      isEmpty(selectedStainSlideIds)
    ) {
      return;
    }

    setSelectedOrchestrations(
      buildSelectedInferenceOrchestrationsFromArtifacts(
        pendingPreviousJobInferenceSelections,
        inferenceArtifactsForSelectedSlides
      )
    );
    setPendingPreviousJobInferenceSelections(null);
  }, [
    inferenceArtifactsForSelectedSlides,
    isWaitingForInferenceArtifacts,
    pendingPreviousJobInferenceSelections,
    selectedStainSlideIds,
    setSelectedOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobPipsSelections === null || isWaitingForPipsResults || isEmpty(selectedStainSlideIds)) {
      return;
    }

    setSelectedPipsOrchestrations(
      buildSelectedPipsOrchestrationsFromArtifacts(
        pendingPreviousJobPipsSelections,
        pipsResultsArtifactsForSelectedSlides
      )
    );
    setPendingPreviousJobPipsSelections(null);
  }, [
    isWaitingForPipsResults,
    pendingPreviousJobPipsSelections,
    pipsResultsArtifactsForSelectedSlides,
    selectedStainSlideIds,
    setSelectedPipsOrchestrations,
  ]);

  useEffect(() => {
    if (pendingPreviousJobInferenceSelections !== null || pendingPreviousJobPipsSelections !== null) {
      return;
    }

    setValue('configParams.cellDetectionArtifactUrlPatterns', cellDetectionArtifactUrlPatterns);
    setIsStepFailed((previousStepFailures) => {
      if (activeStep !== 3 && !(3 in previousStepFailures)) {
        return previousStepFailures;
      }

      return {
        ...previousStepFailures,
        3: Boolean(cellDetectionSelectionError),
      };
    });
    if (cellDetectionSelectionError && activeStep === 3) {
      setError('configParams.cellDetectionArtifactUrlPatterns', {
        type: 'manual',
        message: cellDetectionSelectionError,
      });
    } else if (!cellDetectionSelectionError) {
      clearErrors('configParams.cellDetectionArtifactUrlPatterns');
    }
  }, [
    activeStep,
    cellDetectionArtifactUrlPatterns,
    cellDetectionSelectionError,
    clearErrors,
    pendingPreviousJobInferenceSelections,
    pendingPreviousJobPipsSelections,
    setError,
    setValue,
  ]);

  const onSelectedJobParamChange = (newValue: CellDetectionDependentJob) => {
    if (isEmpty(newValue)) {
      setPendingPreviousJobInferenceSelections(null);
      setPendingPreviousJobPipsSelections(null);
      setSelectedOrchestrations({});
      setSelectedPipsOrchestrations({});
      setSelectedJobId(undefined);
      reset(defaultValues);
    } else {
      setSelectedJobId(newValue.id);
      setPendingPreviousJobInferenceSelections(newValue?.params?.cellDetectionArtifactUrlPatterns || []);
      setPendingPreviousJobPipsSelections(newValue?.params?.cellDetectionArtifactUrlPatterns || []);
      reset({
        jobName: newValue?.name,
        jobDescription: newValue?.description,
        configParams: newValue?.params,
      });
    }
  };

  const selectInferenceResults = (
    slideIds: string[],
    model?: ModelInference,
    selectedModelType?: string,
    orchestration?: ModelInference['orchestrations'][number]
  ) => {
    if (!isEmpty(model) && !isEmpty(orchestration)) {
      setSelectedPipsOrchestrations((currentSelections) => {
        const nextSelections = { ...currentSelections };
        forEach(slideIds, (slideId) => {
          nextSelections[slideId] = {};
        });
        return nextSelections;
      });
    }

    setSelectedOrchestrationsByTypeByStain(slideIds, model, selectedModelType, orchestration);
  };

  const selectPostProcessingResults = (
    slideIds: string[],
    resultKey?: string,
    orchestration?: ResultNameOrchestrations['orchestrations'][number]
  ) => {
    if (!isEmpty(resultKey) && !isEmpty(orchestration)) {
      setSelectedOrchestrations((currentSelections) => {
        const nextSelections = { ...currentSelections };
        forEach(slideIds, (slideId) => {
          nextSelections[slideId] = {};
        });
        return nextSelections;
      });
    }

    updateSelectedPipsOrchestrations(slideIds, resultKey, orchestration);
  };

  const { hasPreviousJobs, isCheckingPreviousJobs } = usePreviousJobsAvailability(jobType);

  const steps = [
    {
      label: 'Reuse previous job settings',
      optional: true,
      hidden: !isCheckingPreviousJobs && !hasPreviousJobs,
      isLoading: isLoadingSelectedPreviousJob,
      content: (
        <LoadFromPreviousJobStep
          jobType={jobType}
          onSelectedJob={(job) => onSelectedJobParamChange(job as CellDetectionDependentJob)}
          onSelectedJobLoadingChange={setIsLoadingSelectedPreviousJob}
          selectedJobId={selectedJobId}
        />
      ),
    },
    {
      label: 'Job Name and Description',
      subLabel: activeStep !== 1 && watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
    {
      label: 'Select Stain and Model',
      subLabel: watch('configParams.modelPath'),
      content: (
        <SelectModelStep<GenericInferenceDependantJobValues, 'configParams.modelPath', undefined, 'stainTypeId'>
          modelUrlField="configParams.modelPath"
          casesParams={casesParams}
          formMethods={form}
          modelType={modelType.apiModelValue}
          slideStainField="stainTypeId"
          onSelectStain={(nextStainTypeId) => {
            if (!isEmpty(selectedStainTypeId) && selectedStainTypeId !== nextStainTypeId) {
              setSelectedOrchestrations({});
              setSelectedPipsOrchestrations({});
            }
          }}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(2),
    },
    {
      label: 'Select Cell Detection Sources',
      content: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose one cell detection source per slide.
            </Typography>
            {isInferenceArtifactsError && <Typography color="error">Error fetching inference artifacts</Typography>}
            {isPipsResultsArtifactsError && (
              <Typography color="error">Error fetching post-processed detections</Typography>
            )}
            <InferenceModelsForSlides
              studyId={studyId}
              slides={slidesForSelectedStain}
              modelsType={getModelsTypeByModelInferences(inferenceArtifactsForSelectedSlides)}
              inferenceModels={inferenceArtifactsForSelectedSlides}
              isLoading={isSlidesFetching}
              isLoadingInferenceResults={isWaitingForInferenceArtifacts}
              pipsResultsArtifacts={pipsResultsArtifactsForSelectedSlides}
              displayPipsResults
              isLoadingPipsResults={isWaitingForPipsResults}
              showValidationMessages={false}
              {...orchestrationSelectionState}
              setSelectedOrchestrationsByTypeByStain={selectInferenceResults}
              updateSelectedPipsOrchestrations={selectPostProcessingResults}
            />
          </Grid>
          <Grid item>
            <Autocomplete
              multiple
              options={commonCellClasses ?? []}
              value={watch('configParams.includeCellClasses')}
              onChange={(_, value) => setValue('configParams.includeCellClasses', value, { shouldValidate: true })}
              getOptionLabel={(option) => humanize(option)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Include Cell Classes"
                  error={!!get(errors, 'configParams.includeCellClasses')}
                  helperText={
                    get(errors, 'configParams.includeCellClasses')?.message ?? !isEmpty(missingCellClasses)
                      ? 'There are some classes that are not in all models, are you sure this is intended?'
                      : ''
                  }
                  variant="outlined"
                />
              )}
              disableCloseOnSelect
              loading={isWaitingForInferenceArtifacts || isWaitingForPipsResults || isSlidesFetching}
            />
          </Grid>
        </Grid>
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(3),
    },
  ];

  return (
    <JobWithRebuild
      jobId={jobId}
      onSelectedJobParamChange={(job) => onSelectedJobParamChange(job as CellDetectionDependentJob)}
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <ExecutionRequirementsPanel requirements={executionRequirements.requirements} />
        </Grid>
        <Grid item>
          <ActionStepper
            hideExecution={!executionRequirements.canExecute}
            handleSubmit={handleSubmit(onSubmit)}
            steps={steps}
            setActiveStepForValidation={setActiveStep}
            isStepFailed={isStepFailed}
            validateStep={() => checkValidationAndSetIsStepFailed(activeStep)}
            isInitializing={isCheckingPreviousJobs}
            initializingText="Checking for previous jobs…"
            incompleteStepsMessage={incompleteStepsMessage}
          />
        </Grid>
      </Grid>
    </JobWithRebuild>
  );
};
